import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { auth } from "../../firebase";
import "../community.css";
import "../ai.css";


import LinkedInComplianceModal from "./LinkedInComplianceModal";

const ONE_DAY = 24 * 60 * 60 * 1000;

function shouldShowModal(key) {
  const lastSeen = localStorage.getItem(key);
  if (!lastSeen) return true;
  return Date.now() - Number(lastSeen) > ONE_DAY;
}

function markModalSeen(key) {
  localStorage.setItem(key, Date.now().toString());
}


function LinkedInGenerator() {
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL;

  const uid = auth.currentUser?.uid || "guest";
const modalKey = `linkedin_compliance_${uid}`;


  // =======================
  // FORM & STATE
  // =======================
  const [form, setForm] = useState({
    fullName: "",
    headline: "",
    experience: "",
    skills: "",
    goals: "",
    experienceLevel: "mid-level",
  });

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);

  // =======================
  // COMPLIANCE MODAL STATE
  // =======================
  const [showCompliance, setShowCompliance] = useState(() =>
  shouldShowModal(modalKey)
);
 // open ONCE
  // ❗ REMOVE pendingAction logic entirely
  // const [pendingAction, setPendingAction] = useState(null);

  // =======================
  // FORM CHANGE
  // =======================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // =======================
  // COPY PROFILE
  // =======================
  const copyToClipboard = () => {
    if (!profile) return;

    const textBlock = `
Headline:
${profile.headline}

About:
${profile.about}

Experience:
${profile.experience
      ?.map((job) => `• ${job.role}\n  - ${job.bullets.join("\n  - ")}`)
      .join("\n\n")}

Skills:
${profile.skills?.join(", ")}

Profile Score: ${profile.score}/100

Improvements:
${profile.improvements?.map((x) => `• ${x}`).join("\n")}
    `.trim();

    navigator.clipboard.writeText(textBlock);
    alert("Copied to clipboard!");
  };

  // =======================
  // PDF DOWNLOAD
  // =======================
  const downloadPDF = () => {
    // NO MORE modal trigger
    handleDownloadPDF();
  };

  const handleDownloadPDF = async () => {
    if (!profile) return alert("Generate your profile first.");

    try {
      const res = await axios.post(`${apiBase}/api/linkedin-pdf`, { profile });

      if (res.data.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
      } else {
        alert("PDF generation failed.");
      }
    } catch (err) {
      console.error("PDF Error:", err);
      alert("PDF generation failed.");
    }
  };

  // =======================
  // GENERATE PROFILE
  // =======================
  const generateProfile = () => {
    // NO MORE modal trigger
    startProfileGeneration();
  };

  const startProfileGeneration = async () => {
    const user = auth.currentUser;
    if (!user) return alert("You must be logged in.");
    if (!form.fullName.trim()) return alert("Enter your full name.");
    if (!form.headline.trim()) return alert("Enter a desired headline.");

    setLoading(true);

    try {
      const resumeBlob = `
Full Name: ${form.fullName}
Headline Goal: ${form.headline}
Experience: ${form.experience}
Skills: ${form.skills}
Career Goals: ${form.goals}
      `.trim();

      const requestBody = {
        resume: resumeBlob,
        jobTitle: form.headline,
        experienceLevel: form.experienceLevel,
        userEmail: user.email,
      };

      const res = await axios.post(
        `${apiBase}/api/ai-linkedin-profile`,
        requestBody
      );

      let parsed;
      try {
        parsed = JSON.parse(res.data.profile);
      } catch (err) {
        console.error("JSON Parse Error:", err);
        alert("AI returned an unexpected response. Try again.");
        setLoading(false);
        return;
      }

      setProfile(parsed);
      setTimeout(() => window.scrollTo(0, document.body.scrollHeight), 300);
    } catch (err) {
      console.error("LinkedIn AI Error:", err.response?.data || err);
      alert(
        err.response?.data?.detail ||
          "Error generating LinkedIn profile. Please try again."
      );
    }

    setLoading(false);
  };

  return (
    <div className="ai-tool-wrapper">

      {/* ===========================
          ONCE-PER-VISIT MODAL
      ============================ */}
      <LinkedInComplianceModal
  visible={showCompliance}
  onAccept={() => {
    markModalSeen(modalKey);
    setShowCompliance(false);
  }}
  onCancel={() => navigate("/community")}
/>


      <h1 className="ai-tool-title">🔗 Professional Profile Builder</h1>
      <Link to="/community">
  <button className="back-btn">← Back to Community</button>
</Link>
      <p className="ai-subtitle">
        Craft a job-winning profile (LinkedIn-optimizer) instantly.
      </p>

      <div className="ai-input-group">
        <input
          name="fullName"
          className="ai-input"
          placeholder="Full Name"
          value={form.fullName}
          onChange={handleChange}
        />

        <input
          name="headline"
          className="ai-input"
          placeholder="Target Job Title / LinkedIn Headline"
          value={form.headline}
          onChange={handleChange}
        />

        <textarea
          name="experience"
          className="ai-input"
          placeholder="Describe your work experience..."
          value={form.experience}
          onChange={handleChange}
        />

        <textarea
          name="skills"
          className="ai-input"
          placeholder="List your top skills..."
          value={form.skills}
          onChange={handleChange}
        />

        <textarea
          name="goals"
          className="ai-input"
          placeholder="Describe your career goals..."
          value={form.goals}
          onChange={handleChange}
        />

        <select
          name="experienceLevel"
          className="ai-input"
          value={form.experienceLevel}
          onChange={handleChange}
        >
          <option value="entry-level">Entry Level</option>
          <option value="mid-level">Mid Level</option>
          <option value="senior-level">Senior Level</option>
        </select>
      </div>

      <button className="ai-btn" onClick={generateProfile} disabled={loading}>
        {loading ? "Building Profile..." : "Generate Profile"}
      </button>

      {/* OUTPUT */}
      {profile && (
        <div className="ai-output">
          <h3
            className="ai-output-title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Your LinkedIn Profile
            <div>
              <button
                onClick={copyToClipboard}
                style={{
                  marginRight: "10px",
                  padding: "6px 12px",
                  background: "#4b6cff",
                  color: "white",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                📋 Copy
              </button>

              <button
                onClick={downloadPDF}
                style={{
                  padding: "6px 12px",
                  background: "#0aa36f",
                  color: "white",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                📄 PDF
              </button>
            </div>
          </h3>

          <div className="ai-section">
            <strong>Headline:</strong>
            <p>{profile.headline}</p>
          </div>

          <div className="ai-section">
            <strong>About:</strong>
            <p>{profile.about}</p>
          </div>

          <div className="ai-section">
            <strong>Experience:</strong>
            {profile.experience?.map((job, i) => (
              <div key={i} className="ai-job">
                <p>
                  <strong>{job.role}</strong>
                </p>
                <ul>
                  {job.bullets?.map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="ai-section">
            <strong>Skills:</strong>
            <p>{profile.skills?.join(", ")}</p>
          </div>

          <div className="ai-section">
            <strong>Profile Score:</strong>
            <p>{profile.score}/100</p>
          </div>

          <div className="ai-section">
            <strong>Improvement Suggestions:</strong>
            <ul>
              {profile.improvements?.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default LinkedInGenerator;











